import { Mutex, MutexInterface } from "async-mutex";
import { logger } from "../../datadog/logger";

/** Adds a final `name: string` param to the given methods for logging. */
type WithNamedCalls<T, K extends keyof T> = Omit<T, K> & {
  [P in K]: T[P] extends (...args: infer A) => infer R
    ? (...args: [...A, string]) => R
    : T[P];
};
type DebugMutexInterface = WithNamedCalls<
  MutexInterface,
  "runExclusive" | "acquire"
>;

export class DebugMutex implements DebugMutexInterface {
  private name: string;
  private mutex = new Mutex();
  private currentHolder: string | null = null;
  private acquiredAt: number | null = null;
  private waiters: string[] = [];

  constructor(name: string) {
    this.name = name;
  }

  async acquire(name: string): Promise<MutexInterface.Releaser> {
    this.waiters.push(name);
    this.currentHolder = name;
    this.acquiredAt = Date.now();

    this.log(`acquire(${name})`);

    const releaser = await this.mutex.acquire();

    return () => {
      this.log(`releasing(${name})`);
      this.waiters = this.waiters.filter((w) => w !== name);
      this.currentHolder = null;
      this.acquiredAt = null;

      return releaser();
    };
  }

  waitForUnlock(): Promise<void> {
    return this.mutex.waitForUnlock();
  }

  isLocked(): boolean {
    return this.mutex.isLocked();
  }

  release(): void {
    return this.mutex.release();
  }

  cancel(): void {
    this.log(`cancel()`);
    this.waiters = [];
    this.currentHolder = null;
    this.acquiredAt = null;

    return this.mutex.cancel();
  }

  async runExclusive<T>(
    callback: MutexInterface.Worker<T>,
    name: string,
  ): Promise<T> {
    this.waiters.push(name);
    this.currentHolder = name;
    this.acquiredAt = Date.now();

    this.log(`runExclusive(${name}): acquiring`);

    try {
      return await this.mutex.runExclusive(callback);
    } finally {
      const heldForMs = Date.now() - this.acquiredAt;

      this.log(`runExclusive(${name}): releasing`, { heldForMs });

      this.waiters = this.waiters.filter((w) => w !== name);
      this.currentHolder = null;
      this.acquiredAt = null;
    }
  }

  log(message: string, context: Record<string, unknown> = {}) {
    logger.info(`[Mutex] ${this.name} ${message}`, {
      currentHolder: this.currentHolder,
      acquiredAt: this.acquiredAt,
      now: Date.now(),
      waiters: [...this.waiters],
      isLocked: this.mutex.isLocked(),
      ...context,
    });
  }
}

/**
 * A mutex to serialize core video SDK operations like audio/video
 * initialization, media element attachment/detachment, joining/leaving rooms,
 * etc. across parts of the application that run concurrently e.g. operations
 * that run reactively and user-initiated operations.
 */
export const videoSDKMutex = new DebugMutex("videoSDKMutex");
